/**
 * CSRF client bootstrap — plan 22.
 *
 * The server (`src/server/middleware/csrf.ts`) implements the double-submit
 * pattern: `GET /api/csrf-token` sets a non-HttpOnly cookie `hd_csrf` AND
 * returns the token in the response body. On every mutation the client
 * must echo the cookie value in the `x-csrf-token` header; the server
 * verifies cookie == header.
 *
 * This module supplies two pieces:
 *   1. `readCsrfCookie()` — a synchronous cookie read used by `safeFetch`
 *      to attach the header on POST/PUT/PATCH/DELETE. Returns `undefined`
 *      when the cookie hasn't been seeded yet (first request after a fresh
 *      page load, before `useCsrfBootstrap` has resolved). The server
 *      exempts `/api/auth/login` and `/api/auth/register`, so a missing
 *      cookie on pre-auth calls is fine.
 *   2. `useCsrfBootstrap()` — a React Query hook mounted in `providers.tsx`
 *      that fires `GET /api/csrf-token` once on app mount. The cookie is
 *      the source of truth for subsequent reads; the query's cached data
 *      is primarily a "bootstrap complete" signal.
 *
 * Design choices:
 *   - Cookie-first (not memoized state) so `safeFetch` can stay a pure
 *     function free of React dependencies.
 *   - `staleTime: 15 min` so we don't hammer `/api/csrf-token` on every
 *     navigation. The cookie lives 24h and the double-submit tolerates
 *     rotation — refreshing once per quarter-hour is a rounding error.
 *   - No `credentials: 'include'` here because we are same-origin under
 *     Vite's dev proxy (plan 21) and the production SPA serves from the
 *     same host — `safeFetch` sets `credentials: 'include'` anyway.
 */
import { useQuery } from '@tanstack/react-query';
import { safeFetch } from './client';
// `readCsrfCookie` lives in src/lib/utils.ts (co-located with safeFetch) to
// keep the import graph acyclic — safeFetch reads the cookie synchronously
// on every mutation and must not bounce through this module.
import { readCsrfCookie } from '../../lib/utils';

const CSRF_ENDPOINT = '/api/csrf-token';
const QUERY_KEY = ['csrfToken'] as const;

export { readCsrfCookie };

/**
 * React Query bootstrap — runs once on mount. Safe to call multiple times;
 * the query key dedupes in-flight requests.
 */
export function useCsrfBootstrap(): ReturnType<typeof useQuery<{ token: string }>> {
  return useQuery<{ token: string }>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const data = await safeFetch(CSRF_ENDPOINT);
      // Server returns `{ token, csrfToken }` — accept either for forward
      // compat. Narrow explicitly instead of trusting `any`.
      const token = (data && (data.token ?? data.csrfToken)) as string | undefined;
      return { token: token ?? '' };
    },
    // The cookie has a 24h max-age but the user may keep a tab open for
    // longer than that, or ITP / private mode may clear it earlier. Two
    // levers keep the cookie warm without hammering the endpoint:
    //   - `refetchOnWindowFocus: true` — a returning tab gets a fresh
    //     token before the user's next mutation submits.
    //   - `staleTime: 10 min` — focus-refetches still no-op while data is
    //     fresh, so flipping between tabs every minute does not refetch.
    // `safeFetch` also self-recovers from a 403 csrf_error by re-seeding
    // the cookie and retrying once, so this hook is the proactive layer
    // and `safeFetch`'s retry is the reactive backstop.
    staleTime: 10 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
  });
}
